import { computed, observable, runInAction } from "mobx";

import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { sharePendingPromise } from "@libs/decorators/sharePendingPromise.ts";
import { EpisodeOfCareDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { Model } from "@libs/models/Model.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";

import { Encounter } from "./Encounter.ts";

enum SortDateLabels {
  RecentAppt = "Last appt",
  RecentConsult = "Last consult",
  InitialConsult = "Initial",
  InjuryDate = "Onset",
  CreatedDate = "Created"
}

interface SortDate {
  date: DateTime;
  label: string;
  sortDate: DateTime;
}

export class EpisodeOfCare extends Model<EpisodeOfCareDto> {
  constructor(
    private root: IRootStore,
    dto: EpisodeOfCareDto
  ) {
    super(dto);
  }

  public get patientId() {
    return this.dto.patientId;
  }

  public get changeLog() {
    return this.dto.changeLog;
  }

  public get primaryDiagnosis() {
    return (
      this.dto.diagnoses?.find(x => x.isPrimaryDiagnosis)?.diagnosisCode
        ?.originalText ?? "Undiagnosed"
    );
  }

  public get diagnosisSide() {
    return this.dto.diagnoses?.find(x => x.isPrimaryDiagnosis)?.diagnosisSide;
  }

  public get diagnoses() {
    return this.dto.diagnoses || [];
  }

  public get injuryDate() {
    return DateTime.fromISO(this.dto.injuryDate);
  }

  public get referralProvider() {
    return this.dto.referralProvider;
  }

  public get referralDate() {
    return DateTime.fromISO(this.dto.referralDate);
  }

  public get referralNumber() {
    return this.dto.referralNumber;
  }

  public get isReferral() {
    return this.dto.isReferral;
  }

  public get discharged() {
    return this.dto.discharged;
  }

  public get isPrivate() {
    return !this.claimEpisodeOfCare || !this.claimEpisodeOfCare?.isActive;
  }

  public get createdDate() {
    return DateTime.fromISO(this.dto.changeLog.createdDate);
  }

  public get claimEpisodeOfCare() {
    return Array.from(this.root.acc.claimEpisodeOfCareMap.values()).find(
      ceoc => ceoc.episodeOfCareId === this.id
    );
  }

  public get inActiveClaim() {
    if (this.claimEpisodeOfCare?.claimId && !this.claimEpisodeOfCare.isActive) {
      return this.root.acc.claimsMap.get(this.claimEpisodeOfCare?.claimId);
    }
    return undefined;
  }

  public get claim() {
    if (
      !this.claimEpisodeOfCare?.claimId ||
      !this.claimEpisodeOfCare.isActive
    ) {
      return undefined;
    }
    return this.root.acc.claimsMap.get(this.claimEpisodeOfCare?.claimId);
  }

  public get patient() {
    return this.root.practice.contactsMap.get(this.patientId);
  }

  public get providerId() {
    return this.claim?.providerId;
  }

  public get sendAccForm() {
    return this.claim?.sendAccForm;
  }

  @sharePendingPromise()
  public async loadClaimEpisodeOfCare(options?: { includeInactive?: boolean }) {
    if (this.claim) return;

    return this.root.acc.getClaimEpisodesOfCare({
      episodesOfCare: [this.id],
      includeInactive: options?.includeInactive
    });
  }

  public async loadClaim() {
    if (this.claim) return;
    if (!this.root.core.hasPermissions(Permission.ClaimRead)) return;
    await this.loadClaimEpisodeOfCare();
    if (this.claimEpisodeOfCare?.claimId) {
      await this.root.acc.getClaim(this.claimEpisodeOfCare.claimId);
    }
  }

  @observable calendarEvents: CalendarEvent[] | undefined;

  private get nextCalendarEvent() {
    return this.calendarEvents?.find(
      calendarEvent => !calendarEvent.startDateTime.isBeforeToday
    );
  }

  public get nextCalendarEventDate() {
    return this.nextCalendarEvent?.startTime;
  }

  public get nextCalendarEventProviderId() {
    return this.nextCalendarEvent?.userId;
  }

  public get initialCalendarEvent() {
    return this.calendarEvents?.[0];
  }

  public get mostRecentCalendarEvent() {
    return Array.from(this.calendarEvents ?? [])
      .reverse()
      .find(
        ({ startDateTime }) =>
          startDateTime.isToday || startDateTime.isBeforeToday
      );
  }

  public loadCalendarEvents = async () => {
    const results = await this.root.booking.ui.getPatientCalendarEventsCached(
      this.patientId
    );

    runInAction(() => {
      this.calendarEvents = results
        .filter(calendarEvent => calendarEvent.episodeOfCareId === this.id)
        .sort((a, b) =>
          compareDatesPredicate(a.startDateTime, b.startDateTime)
        );
    });
  };

  public async loadPatient() {
    await this.root.practice.getContact(this.patientId);
  }

  @computed
  get encounters() {
    return Array.from(this.root.clinical.encounterMap.values())
      .filter(
        encounter => encounter.episodeOfCareId === this.id && encounter.isClosed
      )
      .sort((a, b) =>
        compareDatesPredicate(a.startDateTime, b.startDateTime, true)
      );
  }

  get mostRecentEncounter(): Encounter | undefined {
    return this.encounters[0];
  }

  @computed
  private get sortDates() {
    const arr: SortDate[] = [];

    const recentConsult = this.mostRecentEncounter && {
      date: this.mostRecentEncounter.startDateTime,
      label: SortDateLabels.RecentConsult,
      sortDate: this.mostRecentEncounter.startDateTime
    };

    const recentAppt = this.mostRecentCalendarEvent && {
      date: this.mostRecentCalendarEvent.startDateTime,
      label: SortDateLabels.RecentAppt,
      sortDate: this.mostRecentCalendarEvent.startDateTime
    };

    if (
      recentConsult &&
      recentAppt &&
      recentAppt.sortDate > recentConsult.sortDate
    ) {
      arr.push(recentAppt, recentConsult);
    } else if (recentConsult) {
      arr.push(recentConsult);
    } else if (recentAppt) {
      arr.push(recentAppt);
    }

    if (this.injuryDate) {
      arr.push({
        date: this.injuryDate,
        label: SortDateLabels.InjuryDate,
        sortDate: this.injuryDate
      });
    }

    if (this.claim?.initialConsultDate) {
      arr.push({
        date: this.claim.initialConsultDate,
        label: SortDateLabels.InitialConsult,
        sortDate: this.claim.initialConsultDate
      });
    }

    if (arr.length === 0) {
      const createdDate = this.claim?.createdDate || this.createdDate;

      const pastDate = DateTime.fromISO("2000-01-01");
      const diff = createdDate.diff(pastDate, ["days"]);

      arr.push({
        date: createdDate,
        label: SortDateLabels.CreatedDate,
        sortDate: pastDate.minus({ days: diff.days })
      });
    }

    return arr;
  }

  public get displayDate1(): SortDate {
    return this.sortDates[0];
  }

  public get sortDate() {
    return this.displayDate1.sortDate;
  }

  public get displayDate2(): SortDate | undefined {
    return this.sortDates
      .slice(1)
      .filter(
        item =>
          item.label === SortDateLabels.InjuryDate ||
          item.label === SortDateLabels.InitialConsult
      )[0];
  }
}
